import { ChevronDown, User, Check } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Avatar,
  AvatarFallback,
} from '@/components/ui'
import { useFamilyMembers, useFamily } from '@/hooks'
import { cn } from '@/lib/utils'

interface ParentSelectorProps {
  className?: string
}

export function ParentSelector({ className }: ParentSelectorProps) {
  const { selectedFamilyId } = useFamily()
  const {
    parentMembers,
    selectedMember,
    selectedMemberId,
    setSelectedMemberId,
    isLoading,
  } = useFamilyMembers(selectedFamilyId)

  if (isLoading || parentMembers.length === 0) {
    return null
  }

  // If only one parent, show as static display
  if (parentMembers.length === 1) {
    const parent = parentMembers[0]
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <span className="text-sm text-gray-500">Viewing:</span>
        <div className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-1.5">
          <Avatar className="h-6 w-6">
            <AvatarFallback className="text-xs">
              {getInitials(parent.user.full_name)}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium text-gray-900">
            {parent.user.full_name}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className="text-sm text-gray-500">Viewing:</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 rounded-md border border-gray-200 bg-surface px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
            {selectedMember ? (
              <>
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-xs">
                    {getInitials(selectedMember.user.full_name)}
                  </AvatarFallback>
                </Avatar>
                <span>{selectedMember.user.full_name}</span>
              </>
            ) : (
              <>
                <User className="h-4 w-4 text-gray-400" />
                <span>Select a parent</span>
              </>
            )}
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {parentMembers.map((parent) => (
            <DropdownMenuItem
              key={parent.id}
              onClick={() => setSelectedMemberId(parent.id)}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-xs">
                    {getInitials(parent.user.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="text-sm font-medium">{parent.user.full_name}</div>
                  <div className="text-xs text-gray-500">{parent.user.email}</div>
                </div>
              </div>
              {selectedMemberId === parent.id && (
                <Check className="h-4 w-4 text-primary-600" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

// Tab-style variant for larger displays
interface ParentSelectorTabsProps {
  className?: string
}

export function ParentSelectorTabs({ className }: ParentSelectorTabsProps) {
  const { selectedFamilyId } = useFamily()
  const {
    parentMembers,
    selectedMemberId,
    setSelectedMemberId,
    isLoading,
  } = useFamilyMembers(selectedFamilyId)

  if (isLoading || parentMembers.length === 0) {
    return null
  }

  return (
    <div className={cn('flex items-center gap-1 rounded-lg bg-gray-100 p-1', className)}>
      {parentMembers.map((parent) => {
        const isSelected = selectedMemberId === parent.id
        return (
          <button
            key={parent.id}
            onClick={() => setSelectedMemberId(parent.id)}
            className={cn(
              'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              isSelected
                ? 'bg-surface text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            <Avatar className="h-5 w-5">
              <AvatarFallback className="text-xs">
                {getInitials(parent.user.full_name)}
              </AvatarFallback>
            </Avatar>
            <span>{parent.user.full_name.split(' ')[0]}</span>
          </button>
        )
      })}
    </div>
  )
}
